<template>
  <div>
    <!--
      There is a small problem with this autocomplete - using the pattern we currently have, whenever we update the
      selection, the control is disabled until the update finishes, preventing you from sending too many/conflicting
      requests that would result in an error. This is behavior carried over from an older version of the UI.

      We set the closeOnContentClick property on v-autocomplete, which closes the search menu if something is clicked,
      but this unfortunately does not close the menu when you click on the checkbox next to the item. I am guessing
      this is because you are clicking on the action portion of the v-list item, and not the content, and the handler
      for closeOnContentClick only handles the content portion. There is no documented way to close the menu
      programmatically (a common complaint I have with Vuetify components). There may be an undocumented method, but I
      am not going to bother relying on that. It's also possible that overriding the list presentation to remove the
      action section might work too, but that would require proxying the appropriate v-autocomplete slot.

      This is an unfortunate side effect of what is an unorthodox UI control, I think, but it's okay here because this
      is currently for internal use only. For a more robust solution we'd probably need to redesign the component
      entirely so that the list of customers and the selection process are separate.
    -->
    <vf-autocomplete
      v-model="currentCustomers"
      :loading="updating"
      :disabled="updating"
      :label="$tc('customer', 2)"
      :items="customers"
      item-value="id"
      item-text="name"
      return-object
      menu-props="closeOnClick, closeOnContentClick, closeOnActionClick"
    />
    <v-select
      v-model="primaryCustomer"
      :items="currentCustomers"
      :label="$t('primary_customer')"
      item-value="id"
      item-text="name"
      return-object
      :disabled="updating"/>
    <v-select
      v-model="billingCustomer"
      :items="currentCustomers"
      :label="$t('billing_customer')"
      item-value="id"
      item-text="name"
      return-object
      :disabled="updating"/>
  </div>
</template>

<script>
import VfAutocomplete from '@/components/shared/VfAutocomplete'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import { difference, map } from 'lodash'

export default {
  name: 'VehicleCustomerManagement',
  components: {
    VfAutocomplete
  },
  mixins: [
    CurrentUserMixin,
    ApiUtilitiesMixin
  ],
  props: {
    vehicle: Object
  },
  data () {
    return {
      updating: false
    }
  },
  computed: {
    /**
     * Map the customers into an options array for the pickers.
     * @returns {Array}
     */
    customerOptions () {
      return map(this.customers, c => {
        return {
          value: c.id,
          text: c.name
        }
      })
    },
    currentCustomers: {
      get () {
        return this.vehicle.customers
      },
      /**
       * Calculates the difference between the new set of customers and what is currently on the vehicle, makes the
       * appropriate api call, and updates the local state.
       */
      set: async function (updatedCustomers) {
        this.updating = true
        const customersToLink = difference(updatedCustomers, this.currentCustomers)
        const customersToUnlink = difference(this.currentCustomers, updatedCustomers)
        // We assume that we are only adding or removing one item at a time, as the component is disabled when a change
        // is initiated. Determine what the user did (add or remove) and then process the change
        let action = ''
        let customer = {}
        if (customersToLink.length > 0) {
          action = 'linkCustomer'
          customer = customersToLink[0]
        } else {
          action = 'unlinkCustomer'
          customer = customersToUnlink[0]
        }
        try {
          await this.vehicle[action](customer.id)
          await this.fetchAndUpdateCustomers()
        } catch (error) {
          this.updating = false
          this.handleError(error)
        }
      }
    },
    primaryCustomer: {
      get () {
        return this.vehicle.primary_customer
      },
      /**
       * Triggers an api call and updates the local state when it returns successfully.
       */
      set: async function (customer) {
        if (customer) {
          this.updating = true
          try {
            await this.vehicle.setPrimaryCustomer(customer.id)
            await this.fetchAndUpdateCustomers()
          } catch (error) {
            this.handleError(error)
          } finally {
            this.updating = false
          }
        }
      }
    },
    billingCustomer: {
      get: function () {
        return this.vehicle.billing_customer
      },
      /**
       * Triggers an api call and updates the local state when it returns successfully.
       */
      set: async function (customer) {
        if (customer) {
          this.updating = true
          try {
            await this.vehicle.setBillingCustomer(customer.id)
            await this.fetchAndUpdateCustomers()
          } catch (error) {
            this.handleError(error)
          } finally {
            this.updating = false
          }
        }
      }
    }
  },
  methods: {
    /**
     * Called when we add, remove, or change the primary customer. Fetches an updated list of customer relationships
     * and when complete shows the appropriate banner.
     */
    async fetchAndUpdateCustomers () {
      try {
        const customerRelations = await this.vehicle.fetchCustomersRelations()
        this.vehicle.updateCustomers(customerRelations)
        await this.$store.dispatch(
          'globalMessagingModule/showSuccessFlashBanner',
          `${this.$tc('vehicle')} ${this.vehicle.name} ${this.$t('generic_save_success')}`
        )
        this.updating = false
      } catch (error) {
        this.handleError(error)
      } finally {
        this.updating = false
      }
    }
  }
}
</script>

<style scoped>

</style>
